import React, { useContext, useEffect, useState } from 'react'
import { ResponsiveTableRow, colors } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { css } from '@emotion/core'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import { UserContext } from '../UserContext'
import { getGedcomFiles, getUserGedcomImportResults } from '../../../services/gedcomService'
import { RawData } from '../../common/RawData'
import useStreamDownload from '../../../hooks/useStreamDownload'
import { getBackendBaseUrl } from '../../../util/UrlUtil'
import UserGedcomFileColumn from './UserGedcomFileColumn'
import UserGedcomStatusColumn from './UserGedcomStatusColumn'
import UserGedcomCreatedColumn from './UserGedcomCreatedColumn'
import UserGedcomOwnerColumn from './UserGedcomOwnerColumn'
import UserGedcomActionColumn from './UserGedcomActionColumn'
import useNotifications from '../../../hooks/useNotifications'

const UserGedcom = () => {
  usePageView({ page_detail: 'DMConsole:UserGedcom' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.user.gedcom'))
  const { currentUser } = useContext(UserContext)
  const cisId = currentUser?.dmcUser?.cisId
  const [files, setFiles] = useState([])
  const { streamDataDownload } = useStreamDownload()
  const { error } = useNotifications()

  const warningStyle = css`
    color: ${colors.danger80};
  `
  const wrapLine = css`
    overflow-wrap: break-word;
  `

  const fetchGedcomData = async () => {
    if (!cisId) {
      error(t('gedcom.files.noCisId', 'No CIS ID found for user. Cannot fetch GEDCOM files.'))
      return
    }
    const response = await getGedcomFiles(cisId)
    if (response.status !== 200) {
      error(t('gedcom.files.fetchError', 'Error fetching GEDCOM files'), response)
      return
    }
    await Promise.allSettled(
      response.data.map(async (file) => {
        try {
          const result = await getUserGedcomImportResults(cisId, file.fileName, file.treeId)
          file.importResults = result.data
        } catch (err) {
          error(t('gedcom.files.importResultsError', 'Error fetching import results'), err)
        }
      })
    )
    setFiles(response.data)
  }

  useEffect(() => {
    setFiles([])

    fetchGedcomData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cisId])

  const downloadGedcom = (fileName) => {
    streamDataDownload({
      requestPath: `${getBackendBaseUrl()}/users/${cisId}/gedcom/${fileName}/download`,
    })
  }

  return (
    <>
      {(!files || files.length === 0) && <h3>{t('gedcom.files.empty', 'No Gedcom submissions.')}</h3>}
      {files && files.length > 0 && (
        <FilesList
          files={files}
          wrapLine={wrapLine}
          warningStyle={warningStyle}
          downloadGedcom={downloadGedcom}
          cisId={cisId}
          fetchGedcomData={fetchGedcomData}
        />
      )}
    </>
  )
}

const FilesList = ({ files, wrapLine, warningStyle, downloadGedcom, cisId, fetchGedcomData }) => {
  const { t } = useTranslation()
  return (
    <DmcResponsiveTable
      id="userCtrTable"
      atSizeColumns={{
        default: {
          labels: [t('gedcom.files.fileName', 'File')],
        },
        lg: {
          labels: [
            `${t('gedcom.files.fileName', 'File')}/${t('gedcom.files.dates', 'Created/Modified')}`,
            t('gedcom.files.status', 'Status'),
            t('gedcom.files.owner', 'Owner'),
            '',
          ],
          combinationRules: [['column1', 'column4'], ['column2'], ['column3'], ['column5']],
          columnWidths: [3, 4, 3, 2],
        },
        xl: {
          labels: [
            t('gedcom.files.fileName', 'File'),
            t('gedcom.files.status', 'Status'),
            t('gedcom.files.dates', 'Created/Modified'),
            t('gedcom.files.owner', 'Owner'),
            '',
          ],
          columnWidths: [2, 3, 2, 3, 2],
        },
      }}
      totalColumnCount={5}
      separator={<br />}
    >
      {files.map((file) => (
        <React.Fragment key={file.fileName}>
          <ResponsiveTableRow
            column1={<UserGedcomFileColumn file={file} wrapLine={wrapLine} downloadGedcom={downloadGedcom} />}
            column2={<UserGedcomStatusColumn file={file} warningStyle={warningStyle} />}
            column3={<UserGedcomCreatedColumn file={file} />}
            column4={<UserGedcomOwnerColumn file={file} />}
            column5={<UserGedcomActionColumn file={file} cisId={cisId} fetchGedcomData={fetchGedcomData} />}
          >
            <RawData state={file.importResults} viewJson noExpander />
          </ResponsiveTableRow>
        </React.Fragment>
      ))}
    </DmcResponsiveTable>
  )
}

export default UserGedcom
